import {createAction, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {HwImage, ImageReviewStatusEnum} from 'interfaces/models';
import {AppThunk} from 'app/store';
import {endLoading, startLoading} from 'app/apiSlice';
import {getImages, GetImagesParams, saveImage, uploadImageReplacement} from 'api';
import {SaveImageDataDto, UploadReplacementImageDto} from 'interfaces/dto';
import {rootPath} from 'app/appRoutes';
import errorHandler from '../shared/errorHandler';

interface ImageListState {
    pageCount: number,
    images: HwImage[]
}

const initialState: ImageListState = {
    pageCount: 0,
    images: []
};

const locationChangeAction = createAction<{location: {pathname: string}}>('@@router/LOCATION_CHANGE');

const imageListSlice = createSlice({
    name: 'imageList',
    initialState,
    reducers: {
        setState: (state: ImageListState, action: PayloadAction<ImageListState>) => {
            state.pageCount = action.payload.pageCount;
            state.images = action.payload.images;
        },
        updateImage: (state: ImageListState, action: PayloadAction<SaveImageDataDto>) => {
            const {images} = state;
            const {payload: {image, activities}} = action;
            const idx = images.findIndex(i => i.id === image.imageId);
            if (idx < 0) return;
            
            const img = images[idx];
            img.focalPointX = image.focalPointX;
            img.focalPointY = image.focalPointY;
            img.focalPointZ = image.focalPointZ;
            img.reviewStatus = ImageReviewStatusEnum.ReviewedManual;
            if (activities?.length) {
                activities.forEach(a => {
                    const actIdx = img.activities.findIndex(x => x.activityId === a.activityId && x.referenceKey === a.referenceKey);
                    if (actIdx > -1) {
                        const act = img.activities[actIdx];
                        act.imageType = a.imageType;
                    }
                });
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(locationChangeAction, (state, action) => {
            if (action.payload.location.pathname === rootPath) {
                return initialState;
            }
        })
    }
});

export default imageListSlice.reducer;


export const fetchImages = (params: GetImagesParams): AppThunk<void> => async (dispatch, getState) => {
    const token = getState().oidc.user?.access_token;
    if (!token) return;
    dispatch(startLoading('fetchImages'));
    try {
        const response = await getImages(token, params);
        dispatch(imageListSlice.actions.setState({pageCount: response.pageCount, images: response.data}));
    } catch (err) {
        errorHandler('fetchImages error', err);
    }
    dispatch(endLoading('fetchImages'));
};

export const saveImageData = (data: SaveImageDataDto): AppThunk<void> => async (dispatch, getState) => {
    const token = getState().oidc.user?.access_token;
    if (!token) return;
    dispatch(startLoading('saveImage'));
    try {
        const response = await saveImage(token, data);
        if (response.success)
            dispatch(imageListSlice.actions.updateImage(data));
    } catch (err) {
        errorHandler('saveImage error', err);
    }
    dispatch(endLoading('saveImage'));
};

export const saveImageReplacement = (dto: UploadReplacementImageDto): AppThunk<void> => async (dispatch, getState) => {
    const token = getState().oidc.user?.access_token;
    if (!token) return;
    dispatch(startLoading('uploadImageReplacement'));
    try {
        const response = await uploadImageReplacement(token, dto);
        if (response.success) {
            // todo
        }
    } catch (err) {
        errorHandler('uploadImageReplacement error', err);
    }
    dispatch(endLoading('uploadImageReplacement'));
};